import React, { FunctionComponent } from 'react';

import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { DesktopAlert } from 'admin/notifications/Notifications.types';
import { getActionLabelKey, getAlertIcon } from 'common/notification-tray/desktop-alert-registry/DesktopAlertRegistry';
import { useNotificationTray } from 'common/notification-tray/NotificationTrayContext';

import 'common/notification-tray/desktop-notification-alert/DesktopNotificationAlert.scss';

interface Props {
    alerts: DesktopAlert[];
    onDismiss: (alertId: string) => void;
    onRemindMeLater: (alert: DesktopAlert) => void;
    onAction: (alert: DesktopAlert) => void;
}

interface ParsedPayload {
    text: string;
    subtext?: string;
}

/**
 * Parse alert payload — handles both plain text and JSON payloads.
 * JSON format: { "text": "...", "metadata": { "subtext": "..." } }
 */
const parsePayload = (payload?: string): ParsedPayload | null => {
    if (!payload) return null;

    try {
        const parsed = JSON.parse(payload);

        if (parsed?.text) {
            return {
                text: parsed.text,
                subtext: parsed.metadata?.subtext,
            };
        }
    } catch {
        // Not JSON — treat as plain text
    }

    return { text: payload };
};

const resolveActionLabel = (eventType: string, t: (key: string) => string): string => {
    const labelKey = getActionLabelKey(eventType);

    return labelKey ? t(labelKey) : t('TAKE_ACTION');
};

const resolveIcon = (eventType: string): string => {
    return getAlertIcon(eventType) || 'icon-bell';
};

const resolveFieldLabel = (field: string | undefined, t: (key: string, opts?: object) => string): string => {
    return field ? t(field, { defaultValue: field }) : t('UNKNOWN_FIELD', { defaultValue: 'Field' });
};

export const DesktopNotificationAlert: FunctionComponent<Props> = ({
    alerts,
    onDismiss,
    onRemindMeLater,
    onAction,
}) => {
    const { t } = useTranslation('admin/notifications');
    const { openTray } = useNotificationTray();

    if (!alerts || alerts.length === 0) {
        return null;
    }

    return (
        <div
            className="desktop-notification-alerts"
            role="region"
            aria-label={t('DESKTOP_ALERTS_REGION', { defaultValue: 'Desktop notifications' })}
        >
            {alerts.map((alert) => {
                const eventType = alert.eventType || '';
                const objectIDs = alert.objectIDs || [];
                const notificationHistoryIDs = alert.notificationHistoryIDs || [];
                const iconClass = resolveIcon(eventType);
                const actionLabel = resolveActionLabel(eventType, t);
                const isMultiple = notificationHistoryIDs.length > 1;

                const parsed = parsePayload(alert.payload);

                const entityLabel =
                    eventType === 'FieldsMissingOnProfiles'
                        ? resolveFieldLabel(alert.metadata?.field, t)
                        : alert.metadata?.fieldName;

                const count = objectIDs?.length ?? 0;
                const title =
                    (eventType !== 'FieldsMissingOnProfiles' && parsed?.text) ||
                    t(
                        eventType === 'FieldsMissingOnProfiles' && count > 1
                            ? `${eventType.toUpperCase()}_TITLE_plural`
                            : `${eventType.toUpperCase()}_TITLE`,
                        {
                            entity: entityLabel,
                            count,
                            defaultValue: t('DESKTOP_ALERT_GENERIC_TITLE'),
                        },
                    );

                const impactAnalysis =
                    parsed?.subtext ||
                    alert.impactAnalysis ||
                    alert.metadata?.subtext ||
                    t(`${eventType.toUpperCase()}_MESSAGE`, {
                        entity: entityLabel,
                        defaultValue: t('DESKTOP_ALERT_GENERIC_MESSAGE'),
                    });

                return (
                    <div key={alert.id} className="desktop-notification-alert-card" role="alert" aria-live="polite">
                        <div className="desktop-notification-alert-header">
                            <div className="desktop-notification-alert-icon" aria-hidden="true">
                                <i className={iconClass} />
                            </div>

                            <div className="desktop-notification-alert-title">{title}</div>
                            <button
                                className="desktop-notification-alert-close"
                                onClick={(): void => onDismiss(alert.id)}
                                aria-label={t('DISMISS', { defaultValue: 'Dismiss notification' })}
                                type="button"
                            >
                                <i className="icon-times" aria-hidden="true" />
                            </button>
                        </div>

                        <div className="desktop-notification-alert-analysis">{impactAnalysis}</div>

                        <div className="desktop-notification-alert-actions">
                            {!isMultiple && (
                                <Button
                                    variant="outline-primary"
                                    className="desktop-notification-alert-btn-remind"
                                    onClick={(e): void => {
                                        e.stopPropagation();
                                        onRemindMeLater(alert);
                                    }}
                                    aria-label={t('REMIND_ME_LATER') as string}
                                >
                                    {t('REMIND_ME_LATER')}
                                </Button>
                            )}
                            {isMultiple && (
                                <Button
                                    variant="outline-primary"
                                    className="desktop-notification-alert-btn-remind"
                                    onClick={(e): void => {
                                        e.stopPropagation();
                                        onDismiss(alert.id);
                                        openTray();
                                    }}
                                    aria-label={t('VIEW_ALL_DETAILS', { defaultValue: 'View All Details' })}
                                >
                                    {t('VIEW_ALL_DETAILS', { defaultValue: 'View All Details' })}
                                </Button>
                            )}
                            <Button
                                variant="primary"
                                className="desktop-notification-alert-btn-action"
                                onClick={(e): void => {
                                    e.stopPropagation();
                                    onAction(alert);
                                }}
                                aria-label={actionLabel}
                            >
                                {actionLabel}
                            </Button>
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
